/**
 * Full-page Case Status Change — replaces the CaseDetail.js "Status Change" dialog.
 * Route: /cases/:caseId/status
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Activity, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';
const STATUSES = ['NEW','PENDING_REVIEW','ASSIGNED','ACTIVE','EVIDENCE_COLLECTION','AWAITING_CLIENT_INFO','UNDER_REVIEW','COMPLETED','CLOSED'];

export default function StatusChangePage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);

  const [caseInfo, setCaseInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(c => { setCaseInfo(c); if (c?.status) setNewStatus(c.status); })
      .catch(() => {});
  }, [caseId, token]);

  const goBack = () => navigate(`/cases/${caseId}`);

  const submit = async () => {
    if (!newStatus) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/status?status=${newStatus}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Update failed (${res.status})`);
      toast({ title: 'Status updated', description: newStatus.replace(/_/g, ' ') });
      goBack();
    } catch (e) {
      toast({ title: 'Could not update status', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="status-change-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="status-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6 text-amber-300" /> Change Case Status
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-xl">
        {caseInfo && (
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <span>Current status:</span>
            <Badge className="bg-slate-700 text-slate-100 border-slate-600">
              {(caseInfo.status || '').replace(/_/g, ' ')}
            </Badge>
          </div>
        )}
        <div>
          <Label className="text-slate-300">New Status</Label>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="status-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="status-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving || !newStatus || newStatus === caseInfo?.status}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="confirm-status-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Updating…</> : 'Update Status'}
        </Button>
      </div>
    </div>
  );
}
